import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

const projects = [
  {
    id: 'healthcare-medsync',
    client: 'MedSync Health',
    industry: 'Healthcare',
    project: 'Telemedicine Platform Rebuild',
    tagline: 'Scaling from 5,000 to 50,000 concurrent video sessions with HIPAA compliance in 6 weeks.',
    metric: '+900% concurrent sessions',
    tech: ['AWS ECS', 'WebRTC', 'Node.js', 'React Native'],
    image: 'https://readdy.ai/api/search-image?query=Modern%20telemedicine%20app%20interface%20mockup%20on%20a%20laptop%20screen%20clean%20white%20and%20teal%20medical%20UI%20design%20soft%20ambient%20lighting%20professional%20product%20photography%20style%20minimal%20background&width=600&height=400&seq=sd-recent-1&orientation=landscape',
  },
  {
    id: 'banking-vertex',
    client: 'Vertex Capital',
    industry: 'Fintech',
    project: 'Real-Time Payment Gateway',
    tagline: 'Event-driven T+0 settlement replacing 24-hour batch processing at 12,000 TPS.',
    metric: '12,000 TPS peak throughput',
    tech: ['Java / Spring Boot', 'Apache Kafka', 'PostgreSQL', 'Kubernetes'],
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20fintech%20dashboard%20with%20charts%20and%20payment%20flows%20dark%20navy%20UI%20with%20amber%20accent%20charts%20glowing%20data%20streams%20professional%20digital%20product%20render&width=600&height=400&seq=sd-recent-2&orientation=landscape',
  },
  {
    id: 'retail-luxe',
    client: 'LuxeMarket',
    industry: 'E-Commerce',
    project: 'Omnichannel Commerce Platform',
    tagline: 'Headless checkout rebuilt from 1.2% to 4.8% mobile conversion with 50x flash-sale elasticity.',
    metric: '+300% mobile conversion',
    tech: ['Next.js', 'Node.js', 'Redis', 'Elasticsearch'],
    image: 'https://readdy.ai/api/search-image?query=Sleek%20e-commerce%20website%20displayed%20on%20a%20large%20monitor%20luxury%20product%20grid%20layout%20warm%20neutral%20tones%20with%20gold%20accents%20professional%20studio%20photography%20style%20soft%20shadows&width=600&height=400&seq=sd-recent-3&orientation=landscape',
  },
  {
    id: 'saas-flowstack',
    client: 'FlowStack SaaS',
    industry: 'SaaS',
    project: 'Multi-Tenant Platform Rewrite',
    tagline: 'Cutting onboarding from 3 weeks to 15 minutes and reducing infra cost per tenant by 68%.',
    metric: '-68% infra cost per tenant',
    tech: ['React', 'Node.js', 'PostgreSQL', 'Terraform'],
    image: 'https://readdy.ai/api/search-image?query=SaaS%20product%20dashboard%20with%20user%20analytics%20charts%20clean%20white%20UI%20with%20teal%20accent%20buttons%20modern%20B2B%20software%20interface%20professional%20product%20photography%20on%20minimal%20background&width=600&height=400&seq=sd-recent-4&orientation=landscape',
  },
  {
    id: 'logistics-swift',
    client: 'SwiftFreight',
    industry: 'Logistics',
    project: 'AI Route Optimization Engine',
    tagline: 'Boosting fleet utilization from 62% to 91% and cutting driver idle time by 78%.',
    metric: '+46.8% fleet utilization',
    tech: ['Python', 'TensorFlow', 'React Native', 'PostgreSQL'],
    image: 'https://readdy.ai/api/search-image?query=Logistics%20control%20center%20with%20map%20screens%20showing%20truck%20routes%20modern%20dark%20dashboard%20UI%20with%20green%20route%20lines%20industrial%20warehouse%20atmosphere%20professional%20tech%20photography&width=600&height=400&seq=sd-recent-5&orientation=landscape',
  },
  {
    id: 'manufacturing-aero',
    client: 'AeroForm Industries',
    industry: 'Manufacturing',
    project: 'Predictive Maintenance Platform',
    tagline: 'Unifying 2,400 sensors into a single model that cut unplanned downtime by 74%.',
    metric: '-74% unplanned downtime',
    tech: ['C++', 'Python', 'Azure IoT Hub', 'Grafana'],
    image: 'https://readdy.ai/api/search-image?query=Industrial%20IoT%20dashboard%20showing%20factory%20machine%20telemetry%20dark%20UI%20with%20orange%20warning%20indicators%20manufacturing%20floor%20background%20blurred%20professional%20product%20photography&width=600&height=400&seq=sd-recent-6&orientation=landscape',
  },
];

const VISIBLE_COUNT = 3;

export default function RecentProjectsCarousel() {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.05 });
  const [page, setPage] = useState(0);

  const maxPage = Math.max(0, projects.length - VISIBLE_COUNT);
  const visibleProjects = projects.slice(page, page + VISIBLE_COUNT);

  const nextPage = () => setPage((p) => Math.min(p + 1, maxPage));
  const prevPage = () => setPage((p) => Math.max(p - 1, 0));

  return (
    <section className="py-16 md:py-24 bg-gray-50/50 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10 md:mb-14"
        >
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.1, ...springTransition }}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-full mb-4 md:mb-6 shadow-sm"
            >
              <i className="ri-rocket-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
              <span className="text-sm font-medium text-gray-700">Recent Shipments</span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.15, ...springTransition }}
              className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-navy-900 leading-tight"
            >
              Products We
              <span className="text-primary-600"> Recently Shipped</span>
            </motion.h2>
          </div>

          {/* Nav arrows */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, ...springTransition }}
            className="flex items-center gap-3"
          >
            <button
              onClick={prevPage}
              disabled={page === 0}
              className="w-11 h-11 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Previous projects"
            >
              <i className="ri-arrow-left-line text-lg text-navy-900"></i>
            </button>
            <button
              onClick={nextPage}
              disabled={page >= maxPage}
              className="w-11 h-11 flex items-center justify-center bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Next projects"
            >
              <i className="ri-arrow-right-line text-lg text-navy-900"></i>
            </button>
          </motion.div>
        </motion.div>

        {/* Carousel Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6 mb-8 md:mb-10">
          <AnimatePresence mode="popLayout">
            {visibleProjects.map((project, i) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: -20 }}
                transition={{ delay: i * 0.06, ...springTransition, duration: 0.5 }}
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-primary-200 hover:shadow-xl transition-all duration-500 flex flex-col"
              >
                {/* Image */}
                <div className="relative h-48 md:h-52 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.project}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                    <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-lg text-[11px] font-bold text-navy-900">
                      {project.industry}
                    </span>
                    <span className="px-2.5 py-1 bg-primary-500/90 backdrop-blur-sm rounded-lg text-[11px] font-bold text-white">
                      {project.metric}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 md:p-6 flex-1 flex flex-col">
                  <div className="text-xs font-medium text-gray-400 mb-1">{project.client}</div>
                  <h3 className="text-base md:text-lg font-bold text-navy-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {project.project}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed mb-4 flex-1">
                    {project.tagline}
                  </p>

                  {/* Tech stack */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tech.map((t) => (
                      <span
                        key={t}
                        className="px-2 py-1 bg-gray-50 text-gray-600 text-[11px] font-medium rounded-md border border-gray-100"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={`/case-studies`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors group/link cursor-pointer"
                  >
                    Read case study
                    <i className="ri-arrow-right-line group-hover/link:translate-x-1 transition-transform duration-300"></i>
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2">
          {Array.from({ length: maxPage + 1 }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i)}
              className={`transition-all cursor-pointer ${
                i === page
                  ? 'w-8 h-2.5 bg-primary-500 rounded-full'
                  : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400 rounded-full'
              }`}
              aria-label={`Go to project page ${i + 1}`}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, ...springTransition }}
          className="text-center mt-8 md:mt-10"
        >
          <Link
            to="/case-studies"
            className="group inline-flex items-center gap-2 px-6 py-3 bg-navy-900 text-white text-sm font-semibold rounded-xl hover:bg-navy-800 hover:shadow-lg hover:shadow-navy-900/20 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-folder-chart-line w-5 h-5 flex items-center justify-center"></i>
            View All Case Studies
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}